import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts';
import { useBackHandler } from '@/lib/backHandlerRegistry';
import Spinner from '@/components/Spinner/Spinner';
import {
  ACCOUNT_SETTINGS_ERROR_PARAM,
  ACCOUNT_SETTINGS_OPEN_VALUE,
  ACCOUNT_SETTINGS_PARAM,
  ACCOUNT_SETTINGS_PROVIDER_PARAM,
  clearAccountSettingsOAuthPending,
  clearPendingAccountSettingsLinkFailure,
  consumePendingAccountSettingsSource,
  isAccountSettingsLinkError,
  parseAppRelativePath,
  pathHasAccountSettingsOpen,
  pendingAccountSettingsReturnPath,
  peekPendingAccountSettingsLinkFailure,
  readAccountSettingsOAuthPending,
  withoutAccountSettingsParams,
} from '@/lib/accountSettings';
import { trackAccountSettingsOpened } from '@/lib/accountLinkAnalytics';
import AccountSettingsModal, { type AccountSettingsLinkFailure } from './AccountSettingsModal';

/** Ignore dismissals that land in the same moment the return sheet appears. */
const RETURN_CLOSE_LOCK_MS = 600;

function failureFromSearch(errorParam: string | null, providerParam: string | null): AccountSettingsLinkFailure | null {
  if (!isAccountSettingsLinkError(errorParam)) return null;
  return {
    code: errorParam,
    provider: providerParam === 'google' || providerParam === 'apple' ? providerParam : null,
  };
}

/**
 * Renders Account Settings whenever `?account_settings=open` is on the URL, so menus,
 * deep links, and the OAuth return from a settings Connect all share one modal.
 */
const AccountSettingsHost: React.FC = () => {
  const { user, isInitialized, isLoggedIn } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const wantsOpen = searchParams.get(ACCOUNT_SETTINGS_PARAM) === ACCOUNT_SETTINGS_OPEN_VALUE;
  const errorParam = searchParams.get(ACCOUNT_SETTINGS_ERROR_PARAM);
  const providerParam = searchParams.get(ACCOUNT_SETTINGS_PROVIDER_PARAM);
  const canOpen = Boolean(user) || isLoggedIn;
  const isOpen = wantsOpen && canOpen;
  const [linkFailure, setLinkFailure] = useState<AccountSettingsLinkFailure | null>(null);
  const [oauthReturnPending, setOauthReturnPending] = useState(
    () => Boolean(readAccountSettingsOAuthPending()),
  );
  const [closeLocked, setCloseLocked] = useState(false);
  const closeLockedRef = useRef(false);
  const hasTrackedOpen = useRef(false);
  const onCallback = location.pathname === '/auth/callback';
  const storedFailure = peekPendingAccountSettingsLinkFailure();
  const urlFailure = failureFromSearch(errorParam, providerParam);
  const returningFromConnect = oauthReturnPending || Boolean(storedFailure);
  const expectingFailure = Boolean(urlFailure) || Boolean(storedFailure);
  const sheetReady = isOpen && (!expectingFailure || Boolean(linkFailure));

  // The callback page already shows the spinner. Once that page is gone, cover
  // home until the new sheet can render the collision message. Never navigate
  // off a URL that already has the sheet open — a second replace was landing
  // on home and dropping the message.
  useEffect(() => {
    if (onCallback || !isInitialized || !canOpen) return;
    const settingsReturn = pendingAccountSettingsReturnPath();
    if (!settingsReturn) return;
    const currentPath = `${location.pathname}${location.search}${location.hash}`;
    if (pathHasAccountSettingsOpen(currentPath) || settingsReturn === currentPath) return;
    const target = parseAppRelativePath(settingsReturn);
    navigate({ pathname: target.pathname, search: target.search, hash: target.hash }, { replace: true });
  }, [onCallback, isInitialized, canOpen, location.pathname, location.search, location.hash, navigate]);

  useLayoutEffect(() => {
    if (!isOpen || !linkFailure) {
      closeLockedRef.current = false;
      setCloseLocked(false);
      return;
    }
    closeLockedRef.current = true;
    setCloseLocked(true);
    const timer = window.setTimeout(() => {
      closeLockedRef.current = false;
      setCloseLocked(false);
    }, RETURN_CLOSE_LOCK_MS);
    return () => window.clearTimeout(timer);
  }, [isOpen, linkFailure]);

  useEffect(() => {
    const fromUrl = failureFromSearch(errorParam, providerParam);
    const stored = !fromUrl && wantsOpen && canOpen ? peekPendingAccountSettingsLinkFailure() : null;
    const next = fromUrl ?? (stored ? { code: stored.code, provider: stored.provider } : null);
    if (!next) return;
    setLinkFailure((current) => (
      current?.code === next.code && current.provider === next.provider ? current : next
    ));
  }, [wantsOpen, canOpen, errorParam, providerParam]);

  useEffect(() => {
    if (!wantsOpen) {
      hasTrackedOpen.current = false;
      return;
    }
    if (!canOpen) {
      hasTrackedOpen.current = false;
      return;
    }
    if (!hasTrackedOpen.current) {
      hasTrackedOpen.current = true;
      trackAccountSettingsOpened(consumePendingAccountSettingsSource());
    }
  }, [wantsOpen, canOpen]);

  const acknowledgeLinkFailureDisplayed = useCallback(() => {
    // Keep the stored failure until the user closes the sheet. A later
    // navigation to home can still reopen it with the same message.
    clearAccountSettingsOAuthPending();
    setOauthReturnPending(false);
    setSearchParams((prev) => {
      if (!prev.has(ACCOUNT_SETTINGS_ERROR_PARAM) && !prev.has(ACCOUNT_SETTINGS_PROVIDER_PARAM)) {
        return prev;
      }
      const next = new URLSearchParams(prev);
      next.delete(ACCOUNT_SETTINGS_ERROR_PARAM);
      next.delete(ACCOUNT_SETTINGS_PROVIDER_PARAM);
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const handleOAuthReturnSettled = useCallback(() => {
    clearAccountSettingsOAuthPending();
    setOauthReturnPending(false);
  }, []);

  const close = useCallback(() => {
    if (closeLockedRef.current) return;
    setLinkFailure(null);
    clearPendingAccountSettingsLinkFailure();
    clearAccountSettingsOAuthPending();
    setOauthReturnPending(false);
    setSearchParams((prev) => withoutAccountSettingsParams(prev), { replace: true });
  }, [setSearchParams]);

  useBackHandler(close, isOpen && !oauthReturnPending && !closeLocked);

  // Callback keeps its own spinner. After that, cover the page until the new
  // sheet is ready so home cannot flash and a click cannot dismiss the handoff.
  const showReturnSpinner = !onCallback
    && returningFromConnect
    && !sheetReady
    && !(isInitialized && !canOpen);

  if (showReturnSpinner) return <Spinner />;
  if (!isOpen) return null;
  return (
    <AccountSettingsModal
      onClose={close}
      linkFailure={linkFailure}
      authReady={isInitialized}
      oauthReturnPending={oauthReturnPending}
      preventClose={closeLocked}
      onOAuthReturnSettled={handleOAuthReturnSettled}
      onLinkFailureDisplayed={acknowledgeLinkFailureDisplayed}
    />
  );
};

export default AccountSettingsHost;
